import { Networks } from './network/constants';
import { Account, formatEther, http, type PublicClient } from 'viem';
import { getPublicClient, getViemAddress } from './common/utils/viem-utils';
import { BigNumber, BigNumberish } from './types/bignumber';
import { ErrorHandler } from './error-handler/errorHandler.service';
import { EtherspotBundler } from './bundler/providers/EtherspotBundler';
import { Factory, PaymasterApi, SdkOptions } from "./interfaces";
import { HttpRpcClient } from "./base/HttpRpcClient";
import { getGasFee, UserOperation } from "./common";
import { TransactionDetailsForUserOp, TransactionGasInfoForUserOp } from './base/TransactionDetailsForUserOp';
import { EtherspotWalletAPI } from "./base/EtherspotWalletAPI";
import { VerifyingPaymasterAPI } from "./base/VerifyingPaymasterAPI";
import { BatchUserOpsRequest, UserOpsRequest } from './common/interfaces';
import { isAValidSessionKey } from './session-keys/validate-session-key';
import { signUserOpWithSessionKey } from './session-keys/sign-userop';
import { getOnchainSessionKeyData, isSessionKeyLiveOnChain } from './session-keys/erc20-sessionkey-onchain';
import { SessionKeyOnChainData } from './types';
import { isModuleInstalled, MODULE_TYPE } from './session-keys/module-query';

/**
 * RemoteSigner-Sdk
 *
 * @category RemoteSigner-Sdk
 */
export class RemoteSignerSdk {
  externalViemAccount: Account;
  etherspotWalletAddress: string;
  chainId: number;
  index: number;
  apiKey: string;
  sessionKey: string;
  factoryUsed: string;
  providerUrl: string;
  publicClient: PublicClient;
  etherspotWallet: EtherspotWalletAPI;
  bundler: HttpRpcClient;
  erc20SessionKeyValidator: string;

  private userOpsBatch: BatchUserOpsRequest = { to: [], data: [], value: [] };

  private constructor(externalViemAccount: Account, sdkOptions: SdkOptions) {
    const { index, etherspotWalletAddress, chainId, apiKey, sessionKey, rpcProviderUrl } = sdkOptions;

    if (!externalViemAccount) throw new Error('EOAAddress - ViemAccount object is required');
    this.externalViemAccount = externalViemAccount;

    if (!etherspotWalletAddress) throw new Error('etherspotWalletAddress is required');
    this.etherspotWalletAddress = etherspotWalletAddress;

    if (!chainId || chainId <= 0) throw new Error('chainId is required');

    if (!Networks[chainId]) {
      throw new Error('ChainId not found in the Networks');
    }

    this.chainId = chainId;

    this.index = index ?? 0;

    if (!apiKey) throw new Error('apiKey is required');
    this.apiKey = apiKey;

    if (!sessionKey) throw new Error('sessionKey is required');
    this.sessionKey = sessionKey;

    if (!sdkOptions.bundlerProvider) {
      sdkOptions.bundlerProvider = new EtherspotBundler(chainId);
    }

    this.factoryUsed = sdkOptions.factoryWallet ?? Factory.ETHERSPOT;
    let viemClientUrl = '';

    if (rpcProviderUrl) {
      viemClientUrl = rpcProviderUrl;
    } else {
      viemClientUrl = sdkOptions.bundlerProvider.url;
    }

    this.providerUrl = viemClientUrl;

    this.publicClient = getPublicClient({
      chainId: chainId,
      transport: http(viemClientUrl)
    }) as PublicClient;

    let entryPointAddress = Networks[chainId].contracts.entryPoint;
    if (Networks[chainId].contracts.walletFactory == '') throw new Error('The selected factory is not deployed in the selected chain_id');
    let walletFactoryAddress = Networks[chainId].contracts.walletFactory;

    if (sdkOptions.entryPointAddress) entryPointAddress = sdkOptions.entryPointAddress;
    if (sdkOptions.walletFactoryAddress) walletFactoryAddress = sdkOptions.walletFactoryAddress;

    if (entryPointAddress == '') throw new Error('entryPointAddress not set on the given chain_id');
    if (walletFactoryAddress == '') throw new Error('walletFactoryAddress not set on the given chain_id');
    this.etherspotWallet = new EtherspotWalletAPI({
      sdkOptions,
      entryPointAddress,
      factoryAddress: walletFactoryAddress,
      etherspotWalletAddress: etherspotWalletAddress,
      externalViemAccount: this.externalViemAccount,
      publicClient: this.publicClient,
      index: this.index,
      chainId: this.chainId,
    });
    this.bundler = new HttpRpcClient(sdkOptions.bundlerProvider.url, entryPointAddress, chainId, this.publicClient);
  }

  private async initialize(sdkOptions: SdkOptions): Promise<void> {

    const isPhantom = await this.etherspotWallet.checkAccountPhantom();
    if (isPhantom) {
      throw new Error(`EtherspotWallet: ${this.etherspotWalletAddress} is not deployed/initialized on chain: ${this.chainId}`);
    }

    const erc20SessionKeyValidator = await this.getERC20SessionKeyValidator();
    const isModuleInstalledIndicator = await isModuleInstalled(
      this.publicClient,
      this.etherspotWalletAddress,
      MODULE_TYPE.VALIDATOR,
      erc20SessionKeyValidator);

    if(!isModuleInstalledIndicator) {
      throw new Error(`Module: ${erc20SessionKeyValidator} is not installed on etherspotWalletAddress: ${this.etherspotWalletAddress} on chainId: ${Networks[this.chainId].chain.name}`);
    } 

    const sessionKeyExists = await isAValidSessionKey(this.publicClient, erc20SessionKeyValidator, this.etherspotWalletAddress, this.chainId, this.apiKey, this.sessionKey);

    if (!sessionKeyExists) {
      throw new Error(`Sessionkey: ${sdkOptions.sessionKey} is invalid for etherspotWalletAddress: ${this.etherspotWalletAddress} and apiKey: ${this.apiKey} on chainId: ${Networks[this.chainId].chain.name}`);
    }
  }

  private async getERC20SessionKeyValidator(): Promise<string> {
    if (this.erc20SessionKeyValidator) {
      return this.erc20SessionKeyValidator;
    }

    this.erc20SessionKeyValidator = Networks[this.chainId]?.contracts?.erc20SessionKeyValidator;

    if (!this.erc20SessionKeyValidator) {
      throw new Error('erc20SessionKeyValidator not found in the Networks for chainId: `' + this.chainId + '`');
    }

    return this.erc20SessionKeyValidator;
  }

  static async create(externalViemAccount: Account, sdkOptions: SdkOptions): Promise<RemoteSignerSdk> {
    const instance = new RemoteSignerSdk(externalViemAccount, sdkOptions);
    await instance.initialize(sdkOptions);
    return instance;
  }

  getPublicClient(): PublicClient {
    return this.publicClient;
  }

  getProviderUrl(): string {
    return this.providerUrl;
  }

  async validateSessionKey(): Promise<boolean> {
    const sessionKeyValidator = await this.getERC20SessionKeyValidator();
    return isAValidSessionKey(
      this.publicClient,
      sessionKeyValidator,
      this.etherspotWalletAddress,
      this.chainId,
      this.apiKey,
      this.sessionKey);
  }

  async isSessionKeyLiveOnChain(): Promise<boolean> {
    const sessionKeyValidator = await this.getERC20SessionKeyValidator();
    return isSessionKeyLiveOnChain(
      this.etherspotWalletAddress,
      this.publicClient,
      sessionKeyValidator,
      this.sessionKey);
  }

  async getSessionKeyOnChainData(): Promise<SessionKeyOnChainData> {
    const sessionKeyValidator = await this.getERC20SessionKeyValidator();
    return getOnchainSessionKeyData(
      this.etherspotWalletAddress,
      this.publicClient,
      sessionKeyValidator,
      this.sessionKey);
  }

  async signUserOp(userOp: UserOperation): Promise<UserOperation> {

    if (!this.etherspotWallet.etherspotWalletAddress) {
      throw new ErrorHandler('EtherspotWalletAddress not found', 500);
    }

    const sessionSignedUserOp = await signUserOpWithSessionKey(
      this.etherspotWallet.etherspotWalletAddress,
      this.chainId,
      this.apiKey,
      this.sessionKey,
      userOp
    );

    return sessionSignedUserOp;
  }

  async getCounterFactualAddress(): Promise<string> {
    return this.etherspotWallet.getEtherspotWalletAddress();
  }

  async estimate(params: {
    paymasterDetails?: PaymasterApi,
    gasDetails?: TransactionGasInfoForUserOp,
    callGasLimit?: BigNumberish,
    nonceKey?: BigNumber
  } = { nonceKey: BigNumber.from(0) }): Promise<any> {
    const { paymasterDetails, gasDetails, callGasLimit, nonceKey } = params;
    const dummySignature = "0xfffffffffffffffffffffffffffffff0000000000000000000000000000000007aaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa1c";

    if (this.userOpsBatch.to.length < 1) {
      throw new ErrorHandler('cannot sign empty transaction batch', 1);
    }

    if (paymasterDetails?.url) {
      const paymasterAPI = new VerifyingPaymasterAPI(paymasterDetails.url, this.etherspotWallet.entryPointAddress, paymasterDetails.context ?? {})
      this.etherspotWallet.setPaymasterApi(paymasterAPI)
    } else this.etherspotWallet.setPaymasterApi(null);

    const tx: TransactionDetailsForUserOp = {
      target: this.userOpsBatch.to,
      values: this.userOpsBatch.value,
      data: this.userOpsBatch.data,
      dummySignature: dummySignature,
      ...gasDetails,
    }

    const gasInfo = await this.getGasFee();

    const partialtx = await this.etherspotWallet.createUnsignedUserOp({
      ...tx,
      maxFeePerGas: gasInfo.maxFeePerGas,
      maxPriorityFeePerGas: gasInfo.maxPriorityFeePerGas,
    }, nonceKey);

    if (callGasLimit) {
      partialtx.callGasLimit = BigNumber.from(callGasLimit).toHexString();
    }

    const bundlerGasEstimate = await this.bundler.getVerificationGasInfo(partialtx);

    // if user has specified the gas prices then use them
    if (gasDetails?.maxFeePerGas && gasDetails?.maxPriorityFeePerGas) {
      partialtx.maxFeePerGas = gasDetails.maxFeePerGas;
      partialtx.maxPriorityFeePerGas = gasDetails.maxPriorityFeePerGas;
    }
    // if estimation has gas prices use them, otherwise fetch them in a separate call
    else if (bundlerGasEstimate.maxFeePerGas && bundlerGasEstimate.maxPriorityFeePerGas) {
      partialtx.maxFeePerGas = bundlerGasEstimate.maxFeePerGas;
      partialtx.maxPriorityFeePerGas = bundlerGasEstimate.maxPriorityFeePerGas;
    } else {
      const gas = await this.getGasFee();
      partialtx.maxFeePerGas = gas.maxFeePerGas;
      partialtx.maxPriorityFeePerGas = gas.maxPriorityFeePerGas;
    }

    if (bundlerGasEstimate.preVerificationGas) {
      partialtx.preVerificationGas = BigNumber.from(bundlerGasEstimate.preVerificationGas);
      partialtx.verificationGasLimit = BigNumber.from(bundlerGasEstimate.verificationGasLimit ?? bundlerGasEstimate.verificationGas);
      const expectedCallGasLimit = BigNumber.from(bundlerGasEstimate.callGasLimit);
      if (!callGasLimit)
        partialtx.callGasLimit = expectedCallGasLimit;
      else if (BigNumber.from(callGasLimit).lt(expectedCallGasLimit))
        throw new ErrorHandler(`CallGasLimit is too low. Expected atleast ${expectedCallGasLimit.toString()}`);
    }

    return partialtx;

  }

  async send(signedUserOp: any) {
    return this.bundler.sendUserOpToBundler(signedUserOp);
  }

  async getGasFee() {
    const version = await this.bundler.getBundlerVersion();
    if (version && version.includes('skandha'))
      return this.bundler.getSkandhaGasPrice();
    return getGasFee(this.publicClient);
  }

  async getNativeBalance() {
    const balance = await this.publicClient.getBalance({ address: getViemAddress(this.etherspotWallet.accountAddress) });
    return formatEther(balance);
  }

  async getUserOpReceipt(userOpHash: string) {
    //return this.bundler.getUserOpsReceipt(userOpHash);
    return await this.etherspotWallet.getUserOpReceipt(userOpHash);
  }

  async getUserOpHash(userOp: UserOperation) {
    return this.etherspotWallet.getUserOpHash(userOp);
  }

  async addUserOpsToBatch(
    tx: UserOpsRequest,
  ): Promise<BatchUserOpsRequest> {
    if (!tx.data && !tx.value) throw new ErrorHandler('Data and Value both cannot be empty', 1);
    this.userOpsBatch.to.push(tx.to);
    this.userOpsBatch.value.push(tx.value ?? BigNumber.from(0));
    this.userOpsBatch.data.push(tx.data ?? '0x');
    return this.userOpsBatch;
  }

  async clearUserOpsFromBatch(): Promise<void> {
    this.userOpsBatch.to = [];
    this.userOpsBatch.data = [];
    this.userOpsBatch.value = [];
  }

  async totalGasEstimated(userOp: UserOperation): Promise<BigNumber> {
    const callGasLimit = BigNumber.from(await userOp.callGasLimit);
    const verificationGasLimit = BigNumber.from(await userOp.verificationGasLimit);
    const preVerificationGas = BigNumber.from(await userOp.preVerificationGas);
    return callGasLimit.add(verificationGasLimit).add(preVerificationGas);
  }
}
